<script setup lang="ts">
import { useSiteStore } from '~/stores/site.store';
import { useAuthStore } from '~/stores/auth.store';
import { AccountModalsStrings } from '~/interfaces/dto/general/modals';
import { trackingMethods } from '~/plugins/analytics.client';
import Button from '../design-system/button.vue';
interface IBannerProps {
  vertical?: string;
  desktopAspectRatio?: string;
  mobileAspectRatio?: string;
  displayPromotions?: boolean;
  desktopPlaceholder?: string;
  mobilePlaceholder?: string;
}

const authStore = useAuthStore();
const siteStore = useSiteStore();
const localeStore = useLocaleStore();
const router = useRouter();
const { getPlaceholder } = useBranding();

const activeModal: globalThis.Ref<boolean> = ref<boolean>(false);
const modalContent: globalThis.Ref<string> = ref('');
const noBanners = ref<boolean>(false);
const props = withDefaults(defineProps<IBannerProps>(), {
  vertical: '',
  desktopAspectRatio: '16/3',
  mobileAspectRatio: '36/11',
  displayPromotions: false,
});

function handleBannerClick(action: {
  Action: string;
  Value: string;
  url: string;
}) {
  switch (action.Action) {
    case 'NavigateTo':
    case 'LaunchGame':
      router.push(action.Value.toLowerCase());
      break;
    case 'Launch':
      const detail =
        action.Value === 'Registration' ? 'register' : action.Value;
      AccountModalsStrings.includes(detail.toLowerCase())
        ? authStore.setAccountModalPage(detail.toLowerCase())
        : siteStore.activateModal(detail.toLowerCase());
      break;
  }
  trackingMethods.bannerClicked(action);
}

function handleMoreInfoClick(content: string) {
  modalContent.value = content;
  activeModal.value = true;
  trackingMethods.bannerInfoClicked();
}

const loggedInCompute = computed(() => authStore.loggedIn);
const accessTokenCompute = computed(() =>
  !!authStore.access_token ? `${authStore.access_token}` : '',
);
const playerIdCompute = computed(() =>
  !!authStore.user_id ? authStore.user_id : '',
);

const computedChangeKey = computed(
  () =>
    `${props.vertical}-${
      !!playerIdCompute.value ? playerIdCompute.value : 'playerid-unavailable'
    }-${String(loggedInCompute.value)}-${localeStore.getLocale}`,
);

const bannerPlaceholders = computed(() => {
  return {
    mobile: !!props.mobilePlaceholder
      ? props.mobilePlaceholder
      : getPlaceholder('bannerPlaceholderMobile'),
    desktop: !!props.desktopPlaceholder
      ? props.desktopPlaceholder
      : getPlaceholder('bannerPlaceholderDesktop'),
  };
});
</script>

<template>
  <div>
    <div v-show="!noBanners" class="md:px-0 text-dark-900 dark:text-white">
      <LazyDesignSystemModal
        :title="$t('more-info')"
        :active="activeModal"
        content-type="form"
        @close-modal="[(activeModal = false), (modalContent = '')]"
      >
        <template #modal-content>
          <div v-html="modalContent" class="p-4 dark:text-white" />
        </template>
        <template #footer-content>
          <div class="p-2">
            <Button
              element-name="banner-okay"
              class="w-full capitalize flex-center"
              @click="[(activeModal = false), (modalContent = '')]"
            >
              {{ $t('okay') }}
            </Button>
          </div>
        </template>
      </LazyDesignSystemModal>
      <div
        v-show="!noBanners"
        class="mx-auto pt-2 md:pt-3 md:pb-2 xs:pt-0 xs:pb-0 relative rounded-md"
      >
        <div class="rounded-md relative overflow-hidden">
          <Suspense>
            <LazyBanners
              license="jackpotcityafrica"
              class="container"
              :refetch-key="computedChangeKey"
              :region="siteStore.getRegionCode"
              :vertical="props.vertical"
              :config="{
                aspectRatios: {
                  desktop: `${props.desktopAspectRatio}`,
                  mobile: `${props.mobileAspectRatio}`,
                },
                placeholders: bannerPlaceholders,
                autoplay: 4500,
              }"
              :culture="localeStore.getLocale.split('-')[0].toUpperCase()"
              :player-id="playerIdCompute"
              :access-token="accessTokenCompute"
              :display-placeholder="true"
              :style="{
                aspectRatio: `${
                  $viewport.isLessThan('tablet')
                    ? `${props.mobileAspectRatio}`
                    : `${props.desktopAspectRatio}`
                }`,
              }"
              @banner-click="($event) => handleBannerClick($event)"
              @no-banners="noBanners = $event"
              @info-click="($event) => handleMoreInfoClick($event)"
            >
              <template #placeholder>
                <UserInterfaceGenericSkeleton
                  :style="{
                    aspectRatio: `${
                      $viewport.isLessThan('tablet')
                        ? `${props.mobileAspectRatio}`
                        : `${props.desktopAspectRatio}`
                    }`,
                  }"
                />
              </template>
            </LazyBanners>
          </Suspense>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.carousel_item > img {
  width: 100%;
}
</style>
